/**
 * « 📄 Bons Enfact » — les bons de livraison du jour deviennent des livraisons
 * de la tournée (13/10/2026). Tournée B2B du lundi : crèches de
 * l'Intercommunale, Province (Roisin), CPAS de Quiévrain, Commune de Honnelles.
 *
 * Chaque bon est rattaché à une FICHE CLIENT FOLIES — le lieu de livraison
 * (décision de Philippe : une adresse n'a ni nom ni téléphone, une fiche si).
 * La première fois on choisit la fiche ; ensuite l'historique la propose. Un bon
 * peut se répartir sur plusieurs points (Honnelles : bureau et atelier — le bon
 * ne dit pas ce qui va où) : chaque ligne est attribuée d'un toucher.
 *
 * Rien n'est encaissé ni sorti du stock : la facture Enfact du lendemain s'en
 * charge à son import. Les tickets de colis (QR) s'impriment ici.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';

interface Fiche { id: string; nom: string; telephone?: string | null; adresse?: string | null }
interface LigneBon { index: number; nom: string; quantite: number }
interface BonLu {
  id: number;
  numero: string;
  date: string;
  client_enfact_nom: string;
  client_enfact_adresse: string;
  site: string | null;
  lignes: LigneBon[];
  deja_importe: Array<{ order_id: string; order_number: string; customer_name: string; status: string | null }>;
  /** Fiches proposées : d'après la dernière livraison de ce lieu, sinon par le nom. */
  proposition: { points: Fiche[]; source: 'historique' | 'nom' };
  /** Caisses EPS posées sur le bon (ligne « Vidanges ») : elles donnent le nombre de colis. */
  vidanges?: number;
}

/** Caisses du bon réparties entre ses points (même règle que le serveur, `repartirColis`). */
function colisDuBon(vidanges: number | undefined, points: number): number[] {
  const n = Math.max(1, points);
  const t = Math.floor(Number(vidanges) || 0);
  if (t <= 0) return Array(n).fill(1);
  return Array.from({ length: n }, (_, i) => Math.min(12, Math.max(1, Math.floor(t / n) + (i < t % n ? 1 : 0))));
}
interface Point { fiche: Fiche | null; colis: number }
interface Etat { inclus: boolean; points: Point[] }

interface Props {
  open: boolean;
  date: string;
  onClose: () => void;
  onImporte: () => void;
}

const nomClient = (c: any) => `${c?.first_name ?? ''} ${c?.last_name ?? ''}`.trim() || c?.email || 'Client';
const quantite = (q: number) => String(Math.round(q * 1000) / 1000).replace('.', ',');

/** Recherche d'une fiche client, temporisée — même recherche que « À livrer ». */
function ChoixFiche({ valeur, onChoix }: { valeur: Fiche | null; onChoix: (f: Fiche | null) => void }) {
  const [recherche, setRecherche] = useState('');
  const [resultats, setResultats] = useState<any[]>([]);

  useEffect(() => {
    if (valeur || recherche.trim().length < 2) { setResultats([]); return; }
    const t = setTimeout(() => {
      api.searchCustomers(recherche.trim(), 8)
        .then((r: any) => setResultats((r?.data || r || []) as any[]))
        .catch(() => setResultats([]));
    }, 250);
    return () => clearTimeout(t);
  }, [recherche, valeur]);

  const choisir = async (c: any) => {
    // L'adresse est vérifiée tout de suite : sans elle, l'import serait refusé.
    let adresse: string | null = null;
    try {
      const r: any = await api.get(`/customers/${c.id}/addresses`);
      const liste: any[] = r?.data || r || [];
      const a = liste.find((x) => x.is_default) ?? liste[0];
      adresse = a ? `${a.street}, ${a.postal_code} ${a.city}` : null;
    } catch { /* l'import dira ce qui manque */ }
    onChoix({ id: c.id, nom: nomClient(c), telephone: c.phone ?? null, adresse });
    setRecherche('');
  };

  if (valeur) {
    return (
      <div className="flex items-start justify-between gap-3 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2">
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 truncate">{valeur.nom}</div>
          <div className={`text-sm ${valeur.adresse ? 'text-gray-600' : 'text-red-700 font-medium'}`}>
            {valeur.adresse || 'Pas d’adresse sur cette fiche : ajoutez-la dans Clients'}
          </div>
        </div>
        <button onClick={() => onChoix(null)} className="text-sm text-sky-700 font-semibold shrink-0">Changer</button>
      </div>
    );
  }
  return (
    <div>
      <input
        value={recherche}
        onChange={(e) => setRecherche(e.target.value)}
        placeholder="Fiche client du lieu (nom, téléphone)…"
        className="w-full border border-amber-400 bg-amber-50 rounded-lg px-3 py-2.5"
      />
      {resultats.length > 0 && (
        <ul className="mt-1 border border-gray-200 rounded-lg divide-y bg-white">
          {resultats.map((c) => (
            <li key={c.id}>
              <button onClick={() => choisir(c)} className="w-full text-left px-3 py-2.5 hover:bg-sky-50 flex justify-between gap-3">
                <span className="font-medium truncate">{nomClient(c)}</span>
                <span className="text-sm text-gray-500 shrink-0">{c.phone || ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function BonsEnfactModal({ open, date, onClose, onImporte }: Props) {
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [bons, setBons] = useState<BonLu[]>([]);
  const [etats, setEtats] = useState<Record<number, Etat>>({});
  const [envoi, setEnvoi] = useState(false);
  const [relecture, setRelecture] = useState(0);

  useEffect(() => {
    if (!open) return;
    let annule = false;
    setChargement(true);
    setErreur(null);
    setBons([]);
    api.get('/orders/bons-enfact', { params: { date } })
      .then((r: any) => {
        if (annule) return;
        const lus: BonLu[] = (r?.data || r)?.bons ?? [];
        setBons(lus);
        const init: Record<number, Etat> = {};
        for (const b of lus) {
          const fiches: Array<Fiche | null> = b.proposition.points.length > 0 ? b.proposition.points : [null];
          const colis = colisDuBon(b.vidanges, fiches.length);
          const points: Point[] = fiches.map((f, i) => ({ fiche: f, colis: colis[i] }));
          init[b.id] = { inclus: b.deja_importe.length === 0 && b.lignes.length > 0, points };
        }
        setEtats(init);
      })
      .catch((e: any) => !annule && setErreur(e?.response?.data?.message || 'Bons Enfact illisibles'))
      .finally(() => !annule && setChargement(false));
    return () => { annule = true; };
  }, [open, date, relecture]);

  /** Mauvaise fiche, mauvais jour : l'import est annulé et le bon redevient importable. */
  const annulerImport = async (d: BonLu['deja_importe'][number]) => {
    if (!confirm(`Annuler l'import de ${d.order_number} (${d.customer_name}) ? Le bon pourra être réimporté.`)) return;
    try {
      await api.post(`/orders/bons-enfact/${d.order_id}/annuler`);
      toast.success(`${d.order_number} : import annulé`);
      setRelecture((n) => n + 1);
      onImporte();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Annulation impossible');
    }
  };

  const maj = (id: number, f: (e: Etat) => Etat) => setEtats((s) => ({ ...s, [id]: f(s[id]) }));

  /** Ce qui empêche d'importer un bon coché, à dire en clair. */
  const probleme = (e: Etat | undefined): string | null => {
    if (!e?.inclus) return null;
    if (e.points.some((p) => !p.fiche)) return 'Choisissez la fiche client de chaque point';
    if (e.points.some((p) => p.fiche && !p.fiche.adresse)) return 'Une fiche n’a pas d’adresse';
    const ids = e.points.map((p) => p.fiche!.id);
    if (new Set(ids).size !== ids.length) return 'La même fiche est choisie deux fois';
    return null;
  };

  const choisis = bons.filter((b) => etats[b.id]?.inclus);
  const bloquants = choisis.map((b) => probleme(etats[b.id])).filter(Boolean);
  const totalColis = useMemo(
    () => choisis.reduce((s, b) => s + etats[b.id].points.reduce((t, p) => t + p.colis, 0), 0),
    [choisis, etats],
  );

  if (!open) return null;

  /** Tickets de colis (QR) sur l'EPSON, si la caisse est à côté. Jamais bloquant. */
  const importer = async () => {
    if (choisis.length === 0 || bloquants.length > 0) return;
    setEnvoi(true);
    try {
      const r: any = await api.post('/orders/bons-enfact/importer', {
        date,
        bons: choisis.map((b) => {
          const e = etats[b.id];
          return {
            bl_id: b.id,
            points: e.points.map((p) => ({ customer_id: p.fiche!.id, colis: p.colis })),
          };
        }),
      });
      const res = r?.data || r;
      const crees: any[] = res?.crees ?? [];
      // Plus d'impression ici (26/10/2026) : l'import était lu comme raté quand le
      // poste n'avait pas d'imprimante (« tickets non imprimés »). Les étiquettes se
      // tirent depuis la TOURNÉE (« 🏷️ Étiquettes Zebra »), une fois les arrêts placés.
      toast.success(
        `${crees.length} livraison(s) importée(s)` +
        (res?.sautes?.length ? ` — déjà importés : ${res.sautes.length}` : '') +
        (crees.length > 0 ? '. Placez-les dans une tournée (« Nouvelle tournée »), puis imprimez les étiquettes depuis la tournée.' : ''),
        { duration: 10000 },
      );
      onImporte();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Import impossible');
      setEnvoi(false);
    }
  };

  const jourLisible = new Date(`${date}T12:00:00`).toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="bg-sky-700 px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">📄 Bons de livraison Enfact</h2>
            <p className="text-sky-100 text-sm">Livraison du {jourLisible} — facturés par Enfact : rien à encaisser, stock inchangé</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-3xl leading-none" aria-label="Fermer">×</button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {chargement && <p className="text-gray-600">Lecture des bons chez Enfact…</p>}
          {erreur && <p className="text-red-700 font-medium">{erreur}</p>}
          {!chargement && !erreur && bons.length === 0 && (
            <p className="text-gray-600">Aucun bon de livraison daté du {jourLisible} dans Enfact.</p>
          )}

          {bons.map((b) => {
            const e = etats[b.id];
            if (!e) return null;
            const souci = probleme(e);
            const reparti = e.points.length > 1;
            return (
              <section key={b.id} className={`rounded-xl border-2 p-4 space-y-3 ${e.inclus ? 'border-sky-300' : 'border-gray-200 opacity-70'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={e.inclus}
                    onChange={(ev) => maj(b.id, (x) => ({ ...x, inclus: ev.target.checked }))}
                    className="mt-1 w-5 h-5"
                  />
                  <div className="min-w-0">
                    <div className="font-bold text-gray-900">
                      {b.numero} · {b.site ?? b.client_enfact_nom}
                    </div>
                    <div className="text-sm text-gray-600">
                      {b.site ? `${b.client_enfact_nom} — ` : ''}{b.lignes.length} ligne(s)
                      {(b.vidanges ?? 0) > 0 && ` · ${b.vidanges} caisse(s) EPS sur le bon = colis`}
                    </div>
                    {b.deja_importe.map((d) => (
                      <div key={d.order_id} className="text-sm text-amber-800 font-medium flex items-center gap-3 flex-wrap">
                        <span>Déjà importé : {d.order_number} → {d.customer_name}</span>
                        {d.status === 'ready' && (
                          <button
                            onClick={(ev) => { ev.preventDefault(); annulerImport(d); }}
                            className="text-red-700 underline"
                          >
                            Annuler l’import
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </label>

                {e.inclus && (
                  <>
                    {b.proposition.source === 'nom' && b.proposition.points.length > 0 && (
                      <p className="text-xs text-gray-500">Fiche trouvée d’après le nom du bon — vérifiez-la la première fois.</p>
                    )}
                    {e.points.map((p, i) => (
                      <div key={i} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-700">
                            {reparti ? `Point ${i + 1}` : 'Livré à'}
                          </span>
                          {reparti && (
                            <button
                              onClick={() => maj(b.id, (x) => ({ ...x, points: x.points.filter((_, k) => k !== i) }))}
                              className="text-sm text-red-700"
                            >
                              Retirer ce point
                            </button>
                          )}
                        </div>
                        <ChoixFiche
                          valeur={p.fiche}
                          onChoix={(f) => maj(b.id, (x) => ({ ...x, points: x.points.map((q, k) => (k === i ? { ...q, fiche: f } : q)) }))}
                        />
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-700">Colis</span>
                          <button
                            type="button"
                            onClick={() => maj(b.id, (x) => ({ ...x, points: x.points.map((q, k) => (k === i ? { ...q, colis: Math.max(1, q.colis - 1) } : q)) }))}
                            className="w-11 h-11 rounded-lg border-2 border-gray-300 text-xl font-bold"
                            aria-label="Un colis de moins"
                          >−</button>
                          <span className="w-8 text-center text-xl font-bold tabular-nums">{p.colis}</span>
                          <button
                            type="button"
                            onClick={() => maj(b.id, (x) => ({ ...x, points: x.points.map((q, k) => (k === i ? { ...q, colis: Math.min(12, q.colis + 1) } : q)) }))}
                            className="w-11 h-11 rounded-lg border-2 border-gray-300 text-xl font-bold"
                            aria-label="Un colis de plus"
                          >+</button>
                        </div>
                      </div>
                    ))}

                    {reparti && (
                      <p className="text-sm font-medium text-sky-800 bg-sky-50 rounded-lg px-3 py-2">
                        Bon partagé à parts égales : chaque point reçoit 1/{e.points.length} de chaque article.
                      </p>
                    )}
                    <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                      {b.lignes.map((l) => (
                        <li key={l.index} className="px-3 py-2 flex items-center gap-3">
                          <span className="flex-1 min-w-0 truncate">{l.nom}</span>
                          <span className="tabular-nums text-gray-700 shrink-0">
                            {reparti
                              ? `${quantite(Math.round((l.quantite / e.points.length) * 1000) / 1000)} × ${e.points.length}`
                              : quantite(l.quantite)}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => maj(b.id, (x) => ({ ...x, points: [...x.points, { fiche: null, colis: 1 }] }))}
                      className="text-sky-700 font-semibold text-sm"
                    >
                      + Partager avec un autre point de livraison
                    </button>
                    {souci && <p className="text-sm text-red-700 font-medium">{souci}</p>}
                  </>
                )}
              </section>
            );
          })}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-3 shrink-0">
          <span className="text-sm text-gray-600">
            {choisis.length} bon(s) · {totalColis} ticket(s) de colis
          </span>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-3 rounded-xl border">Annuler</button>
            <button
              onClick={importer}
              disabled={envoi || choisis.length === 0 || bloquants.length > 0}
              className="px-6 py-3 rounded-xl bg-sky-700 text-white font-bold disabled:bg-gray-300"
            >
              {envoi ? 'Import…' : 'Importer et imprimer les tickets'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
